import React, { useRef } from "react";
import { useQuery } from "react-query";
import { ErrorBoundary } from "react-error-boundary";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import {
  getAvailablePluginCategories,
  getPluginFrontendTemplate,
} from "@services/api/plugins";
import {
  pluginCategory,
  pluginInspectorProps,
  eachPluginDataProps,
} from "@pages/homepage/plugins/PluginTypes";
import { ALL_KEYS, getFeatureValues } from "@utils/licenseUtils";

import PluginEnv from "./PluginEnvironment";
import NoData from "@components/common/NoDataLabel";

const PluginEntityInspector: React.FC<pluginInspectorProps> = ({
  mac,
  timeFrame = "day",
  ip,
}) => {
  const pluginTabContentRef = useRef<HTMLDivElement>(null);

  const {
    data: allPluginCategoriesData,
    isLoading: pluginsLoading,
    isFetching: pluginsFetching,
  } = useQuery<pluginCategory[]>(["plugins", mac], () =>
    getAvailablePluginCategories(mac),
  );

  if (pluginsLoading || pluginsFetching) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div
          className={
            "w-8 h-8 border-4 border-gray-600 border-t-blue-400 rounded-full animate-spin"
          }
        />
      </div>
    );
  }

  if (!allPluginCategoriesData) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <NoData />
      </div>
    );
  }

  return (
    <Tabs
      defaultValue={
        allPluginCategoriesData && allPluginCategoriesData.length > 0
          ? allPluginCategoriesData[0].id
          : ""
      }
      className="w-full min-h-80"
    >
      <TabsList
        className="w-[88vw] flex overflow-x-auto whitespace-nowrap overflow-y-hidden inline-block"
        style={{
          width: `${getFeatureValues(ALL_KEYS.ASSET_ENTITY_INSPECTOR).width || "90vw"}`,
        }}
      >
        {allPluginCategoriesData?.map((plugin, index) => (
          <TabsTrigger
            key={index}
            value={plugin.id}
            // className={`entity-plugin-tab-${plugin.id}`}
            className="min-w-[50px]"
          >
            {plugin.name}
          </TabsTrigger>
        ))}
      </TabsList>
      <div ref={pluginTabContentRef}>
        {allPluginCategoriesData?.map((plugin, index) => (
          <TabsContent key={index} value={plugin.id}>
            <ErrorBoundary
              fallback={<div>Something went wrong!</div>}
              onError={(err: any) => console.log(err)}
            >
              <div className="relative">
                <div
                  className={`h-full w-full bg-background entity-plugin-tab-${plugin.id}`}
                >
                  <EachPluginData
                    plugin={plugin}
                    mac={mac}
                    ip={ip}
                    timeFrame={timeFrame}
                  />
                </div>
                {/* <div className="absolute top-1/2 -translate-y-1/2 left-1/2 mt-4 -translate-x-1/2 z-[-1]">
                  -- No Data Found --
                </div> */}
              </div>
            </ErrorBoundary>
          </TabsContent>
        ))}
      </div>
    </Tabs>
  );
};

export default PluginEntityInspector;

const EachPluginData: React.FC<eachPluginDataProps> = ({
  plugin,
  mac,
  ip,
  timeFrame = "day",
}) => {
  const {
    data: eachPluginData,
    isLoading: pluginDataLoading,
    isFetching: pluginDataFetching,
  } = useQuery<pluginCategory>(
    ["pluginData", mac, plugin.id],
    () =>
      getPluginFrontendTemplate(
        plugin.id,
        mac,
        ip,
        plugin.details?.template,
        plugin.details?.data_function,
        timeFrame,
      ),
    {
      retry: 1,
    },
  );

  if (pluginDataLoading || pluginDataFetching) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div
          className={
            "w-8 h-8 border-4 border-gray-600 border-t-blue-400 rounded-full animate-spin"
          }
        />
      </div>
    );
  }

  if (!eachPluginData) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <NoData />
      </div>
    );
  }

  return <PluginEnv htmlContent={eachPluginData} />;
};
